import { useMemo } from 'react'
import { Box, Button, Flex, Spinner, Stack, Textarea, Text } from '@chakra-ui/react'
import { marked } from 'marked'
import { LuRotateCcw } from 'react-icons/lu'

import { DEFAULT_INFO_MARKDOWN } from '../constants/defaultInfoMarkdown'
import { useTranslation } from '../i18n'

const markdownPreviewCss = {
    '& h1': { fontSize: '1.5rem', fontWeight: 700, marginTop: '1rem', marginBottom: '0.5rem' },
    '& h2': { fontSize: '1.25rem', fontWeight: 700, marginTop: '1rem', marginBottom: '0.5rem' },
    '& h3': { fontSize: '1.05rem', fontWeight: 700, marginTop: '0.75rem', marginBottom: '0.5rem' },
    '& h4, & h5, & h6': { fontSize: '1rem', fontWeight: 700, marginTop: '0.75rem', marginBottom: '0.5rem' },
    '& p': { marginBottom: '0.5rem', lineHeight: 1.6 },
    '& ul': { paddingLeft: '1.5rem', marginBottom: '0.75rem', listStyle: 'disc' },
    '& ol': { paddingLeft: '1.5rem', marginBottom: '0.75rem', listStyle: 'decimal' },
    '& li': { marginBottom: '0.25rem', lineHeight: 1.5 },
    '& a': { color: 'var(--mc-link)', textDecoration: 'underline' },
    '& strong': { fontWeight: 700 },
    '& em': { fontStyle: 'italic' },
    '& blockquote': {
        borderLeft: '3px solid var(--mc-link)',
        paddingLeft: '0.75rem',
        margin: '0.5rem 0 0.75rem',
        fontStyle: 'italic',
        opacity: 0.85,
    },
    '& code': {
        background: 'var(--mc-code-bg)',
        padding: '0.1em 0.35em',
        borderRadius: '4px',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '0.9em',
    },
    '& pre': {
        background: 'var(--mc-pre-bg)',
        padding: '0.6rem 0.8rem',
        borderRadius: '6px',
        overflowX: 'auto',
        fontSize: '0.85rem',
        marginBottom: '0.75rem',
    },
    '& hr': { border: 'none', borderTop: '1px solid var(--mc-border)', margin: '1rem 0' },
}

function Preview({ html }) {
    return (
        <Box
            flex="1"
            minW="0"
            p={3}
            bg="var(--mc-preview-bg)"
            borderRadius="md"
            border="1px solid"
            borderColor="border.muted"
            maxH="60vh"
            overflowY="auto"
            css={markdownPreviewCss}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    )
}

export default function EventInfo({
    loading,
    editing,
    canEdit,
    markdown,
    onChange,
}) {
    const { t } = useTranslation()
    const previewHtml = useMemo(() => marked.parse(markdown || '', { gfm: true, breaks: false }), [markdown])

    return (
        <>
            {loading ? (
                <Flex align="center" gap={2}><Spinner size="sm" /><Text>{t('common.loading')}</Text></Flex>
            ) : (
                <Stack gap={3}>
                    <Text fontSize="sm" color="fg.muted">{t('events.infoSectionHelp')}</Text>

                    {editing ? (
                        <Flex gap={3} align="stretch" direction={{ base: 'column', md: 'row' }}>
                            <Textarea
                                flex="1"
                                minW="0"
                                value={markdown ?? ''}
                                onChange={(e) => onChange?.(e.target.value)}
                                rows={20}
                                fontFamily="'JetBrains Mono', monospace"
                                fontSize="sm"
                                placeholder={t('events.infoPlaceholder')}
                                resize="vertical"
                            />
                            <Preview html={previewHtml} />
                        </Flex>
                    ) : (
                        <Preview html={previewHtml} />
                    )}

                    {editing && canEdit && (
                        <Flex justify="flex-end">
                            <Button size="sm" variant="ghost" onClick={() => onChange?.(DEFAULT_INFO_MARKDOWN)}>
                                <LuRotateCcw /> {t('events.infoResetDefault')}
                            </Button>
                        </Flex>
                    )}
                </Stack>
            )}
        </>
    )
}
